// Extra CMS slots for the 'footer' page — added to make previously-hardcoded
// public marketing copy editable. Concatenated into CMS_SLOTS via ./index.
// Each default MUST equal the exact copy the component rendered before wiring,
// so the public page is byte-identical until an admin edits it.
//
// Link hrefs/routes stay HARDCODED in SiteFooter.tsx; only the visible LABEL of
// each link is editable here. Each label slot maps statically to one fixed
// route in the component (never index-zipped to a route array).
import type { SlotDef } from '../slots';

export const footerExtra: SlotDef[] = [
  // ─── Column headings ────────────────────────────────────────────────────
  // Retired 2026-07-11 (Richie: "REMOVE LINE"). The footer link columns now render
  // heading-less; slots kept (append-only) but hidden from the CMS + no longer read.
  {
    key: 'footer.heading_product',
    page: 'footer',
    kind: 'markdown',
    label: 'Product column heading',
    default: 'Product',
    deprecated: true,
  },
  {
    key: 'footer.heading_trust',
    page: 'footer',
    kind: 'markdown',
    label: 'Trust column heading',
    default: 'Trust',
    deprecated: true,
  },
  {
    key: 'footer.heading_legal',
    page: 'footer',
    kind: 'markdown',
    label: 'Legal column heading',
    default: 'Legal',
    deprecated: true,
  },
  // ─── Product column link labels ─────────────────────────────────────────
  {
    key: 'footer.link_browse',
    page: 'footer',
    kind: 'markdown',
    label: 'Link label: Browse venues',
    helpText: 'Product-column footer link to /browse.',
    default: 'Browse venues',
  },
  {
    key: 'footer.link_explore',
    page: 'footer',
    kind: 'markdown',
    label: 'Link label: Explore venues',
    helpText: 'Product-column footer link to /explore.',
    default: 'Explore venues',
    // Retired 2026-07-06 (Richie: "Remove this??"). The /explore link no longer
    // renders in the footer; kept as a deprecated row so it's hidden from the
    // CMS admin and any stray override doesn't orphan.
    deprecated: true,
  },
  {
    key: 'footer.link_events',
    page: 'footer',
    kind: 'markdown',
    label: 'Link label: Events',
    helpText: 'Product-column footer link to /events (signed-in footer only).',
    default: 'Events',
  },
  {
    key: 'footer.link_how_it_works',
    page: 'footer',
    kind: 'markdown',
    label: 'Link label: How it works',
    helpText: 'Product-column footer link to /how-it-works.',
    default: 'How it works',
  },
  {
    key: 'footer.link_founding',
    page: 'footer',
    kind: 'markdown',
    label: 'Link label: Founding venues',
    helpText: 'Product-column footer link to /founding-venue-offer.',
    default: 'Founding venues',
  },
  {
    key: 'footer.link_sign_up',
    page: 'footer',
    kind: 'markdown',
    label: 'Link label: Sign up',
    helpText: 'Product-column footer link to /sign-up (public footer only).',
    default: 'Sign up',
  },
  // ─── Trust column link labels ───────────────────────────────────────────
  // Renamed 2026-08-18: the page is called "Our Ethos" now. This footer link is
  // also one of only two remaining signposts to it (the other being the "Read
  // on" cards) since Our Ethos came off the top nav the same day, so the label
  // has to match what the page calls itself.
  {
    key: 'footer.link_what_we_are',
    page: 'footer',
    kind: 'markdown',
    label: 'Link label: Our Ethos',
    helpText: 'Trust-column footer link to /what-we-are-and-arent.',
    default: 'Our Ethos',
  },
  {
    key: 'footer.link_community_guidelines',
    page: 'footer',
    kind: 'markdown',
    label: 'Link label: Community guidelines',
    helpText: 'Trust-column footer link to /community-guidelines.',
    default: 'Community guidelines',
  },
  {
    key: 'footer.link_dispute_resolution',
    page: 'footer',
    kind: 'markdown',
    label: 'Link label: Dispute resolution',
    helpText: 'Trust-column footer link to /dispute-resolution.',
    default: 'Dispute resolution',
  },
  // Renamed 2026-07-27 (/notes-from-the-road became /ripples); the default was
  // left on the old name until 2026-08-18. It matters more now than it did then:
  // Ripples came off the top nav, so this footer link and the "Read on" cards
  // are the only two remaining routes to the page.
  {
    key: 'footer.link_notes',
    page: 'footer',
    kind: 'markdown',
    label: 'Link label: Ripples',
    helpText: 'Trust-column footer link to /ripples.',
    default: 'Ripples',
  },
  {
    key: 'footer.link_recaps',
    page: 'footer',
    kind: 'markdown',
    label: 'Link label: Recaps',
    helpText: 'Trust-column footer link to /recaps.',
    default: 'Recaps',
    // Removed from the footer (feedback Yta6p81ALHJp1k_YfVugn) — read as an
    // obsolete duplicate in the trust column. Kept as a deprecated row so any
    // existing override doesn't orphan; the /recaps page itself still exists.
    deprecated: true,
  },
  {
    key: 'footer.link_faq',
    page: 'footer',
    kind: 'markdown',
    label: 'Link label: FAQ',
    helpText: 'Trust-column footer link to /faq.',
    default: 'FAQ',
  },
  // ─── Legal column link labels ───────────────────────────────────────────
  {
    key: 'footer.link_about',
    page: 'footer',
    kind: 'markdown',
    label: 'Link label: About',
    helpText: 'Legal-column footer link to /about.',
    default: 'About',
  },
  {
    key: 'footer.link_privacy',
    page: 'footer',
    kind: 'markdown',
    label: 'Link label: Privacy',
    helpText: 'Legal-column footer link to /privacy.',
    default: 'Privacy',
  },
  {
    key: 'footer.link_terms',
    page: 'footer',
    kind: 'markdown',
    label: 'Link label: Terms',
    helpText: 'Legal-column footer link to /terms.',
    default: 'Terms',
  },
  // ─── Social column ───────────────────────────────────────────────────────
  {
    key: 'footer.heading_social',
    page: 'footer',
    kind: 'markdown',
    label: 'Social column heading',
    default: 'Follow',
  },
  // ─── Acknowledgement of Country ─────────────────────────────────────────
  {
    key: 'footer.acknowledgement_of_country',
    page: 'footer',
    kind: 'markdown',
    label: 'Acknowledgement of Country',
    helpText:
      'Sensitive cultural content. Do not reword without checking with Traditional Owner representatives first.',
    default:
      'wwaave acknowledges the Kabi Kabi (Gubbi Gubbi) and Jinibara peoples, Traditional Custodians of the lands on which wwaave is built, and pays respect to Elders past, present and emerging. We extend that respect to all First Nations peoples across the lands and waters where our community creates and gathers.',
  },
];
